<script lang="ts">
    import { afterUpdate } from 'svelte';
    import Emoji from '../../Emoji.svelte';
    import StatusIcon from './StatusIcon.svelte';
    import clsx from 'clsx';

    // Utils
    import { getBorderRadius } from '$lib/global/helper/border-radius';

    // Types
    import type { HTMLInputTypeAttribute } from 'svelte/elements';

    export let placeholder = '';
    export let showEmoji = false;
    export let emoji = '';
    export let required = false;
    export let invalid = false;
    export let pristine = true;
    export let touched = false;
    export let active = false;
    export let type = 'text';
    export let isFirst = false;
    export let isLast = false;
    export let finalFormInput = {};
    export let errorMessage = '';

    let showError = invalid && touched;
    let showSuccess = !invalid && touched && !pristine;
    let showCheckmark = ['email', 'phone', 'url'].includes(type);
    let wrapperClasses: string;
    let inputContainerClasses: string;
    let inputClasses: string;

    const getHtmlInputType = (type: string): HTMLInputTypeAttribute => {
        switch (type) {
            case 'phone':
                return 'tel';
            case 'url':
                return 'text';
            default:
                return type;
        }
    };

    afterUpdate(() => {
        showError = invalid && touched;
        showSuccess = !invalid && touched && !pristine;
        showCheckmark = ['email', 'phone', 'url'].includes(type);
    });

    const borderRadius = getBorderRadius('fieldsWrapper');

    $: wrapperClasses = clsx(
        borderRadius,
        'wrapper flex items-center relative box-border overflow-hidden',
        {
            ['rounded-b-none']: !isLast,
            ['rounded-t-none']: !isFirst,
            ['border-b']: isFirst && isLast,
            invalid: invalid,
        },
    );

    $: inputContainerClasses = clsx(
        borderRadius,
        'input-container box-border w-full px-6 border-0 border-l border-r border-t mb-0 bg-white align-middle transition-all pr-0',
        {
            ['border-black/10']: !invalid,
            ['rounded-b-none']: !isLast,
            ['rounded-t-none']: !isFirst,
            ['border-b']: ((invalid && isFirst) || !isFirst) && isLast,
            ['pl-[70px]']: showEmoji,
            ['h-[90px]']: invalid && showError,
            ['h-[64px]']: !invalid || !showError,
            ['focused bg-gray-50']: active,
        },
    );

    $: inputClasses = clsx(
        'input w-full placeholder-gray-400 font-ui text-base align-middle transition appearance-none font-light bg-transparent border-none outline-none rounded-none',
        {
            'h-full': !invalid || !showError,
            'h-[64px]': invalid && showError,
            'pr-24': !required && pristine,
            'pr-12': invalid && showError,
            'pr-10': (required || !pristine) && (!invalid || !showError),
        },
    );
</script>

<div class={wrapperClasses}>
    {#if showEmoji}
        <div class="absolute left-6 top-0 flex h-[64px] select-none items-center">
            <u class="no-underline">
                <Emoji {emoji} size="24px" />
            </u>
        </div>
    {/if}

    {#if !required && !invalid && pristine}
        <u class="absolute right-6 text-xs font-light text-gray-500 no-underline">(optional)</u>
    {/if}

    <StatusIcon {showCheckmark} {showError} {showSuccess} />

    <div class={inputContainerClasses}>
        <input
            class={inputClasses}
            type={getHtmlInputType(type)}
            pattern={type === 'number' ? '[0-9]*' : undefined}
            {placeholder}
            name={finalFormInput.name}
            value={finalFormInput.value || ''}
            on:blur={finalFormInput.onBlur}
            on:focus={finalFormInput.onFocus}
            on:input={(e) => finalFormInput.onChange(e.target.value)}
        />

        {#if errorMessage && showError}
            <div class="-mt-2.5 text-xs font-normal leading-tight text-[#ef444a]">
                {errorMessage}
            </div>
        {/if}
    </div>
</div>

<style lang="scss">
    .wrapper {
        box-shadow: 0 1px 1px 0 rgba(#000, 0.1);

        :global([placeholder]) {
            @apply truncate;
        }

        .input-container.focused {
            box-shadow: 0 1px 1px 0 rgba(#000, 0.1);
        }
    }
</style>
